import type { MaintenanceResponse } from "@central-hub/contracts";
import { type FormEvent, useState } from "react";
import { ApiError, api } from "./api";
import type { Session } from "./auth";
import { dayClock } from "./format";
import { Icon } from "./icons";

const DURATIONS = [
  { minutes: 30, label: "30 min" },
  { minutes: 60, label: "1 hour" },
  { minutes: 120, label: "2 hours" },
  { minutes: 240, label: "4 hours" },
  { minutes: 24 * 60, label: "1 day" },
];

/**
 * A target's maintenance: whether its alerts are held now (a window, or a deploy) and, for
 * admins, holding them for a while or ending a window early. Incidents are still recorded.
 */
export function MaintenanceSection({
  target,
  session,
  maintenance,
  isAdmin,
  onChange,
}: {
  target: string;
  session: Session;
  maintenance: MaintenanceResponse | null;
  isAdmin: boolean;
  onChange(): void;
}) {
  const [minutes, setMinutes] = useState(60);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = maintenance?.active.find((w) => w.target === target);
  const scheduled = maintenance?.scheduled.filter((w) => w.target === target) ?? [];
  if (!isAdmin && !active && scheduled.length === 0) return null;

  const call = async (run: (token: string) => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const token = await session.getToken();
      if (!token) throw new Error("Your session expired: sign in again.");
      await run(token);
      onChange();
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : "The hub didn’t answer.");
    } finally {
      setBusy(false);
    }
  };

  const hold = (event: FormEvent) => {
    event.preventDefault();
    const text = reason.trim();
    if (!text) return;
    call((token) => api.openMaintenance(token, { target, minutes, reason: text })).then(() =>
      setReason(""),
    );
  };

  return (
    <section className="detail-section maintenance">
      <h3 className="eyebrow">Maintenance</h3>
      {active ? (
        <p className="notice" data-status="unknown">
          <Icon name="clock" />
          <span>
            Alerts held
            {active.until ? ` until ${dayClock(active.until)}` : " while the deploy runs"}:{" "}
            {active.reason}
            {active.createdBy && ` (${active.createdBy})`}. Incidents are still recorded.
            {isAdmin && active.id !== undefined && (
              <>
                {" "}
                <button
                  type="button"
                  className="linkbtn"
                  disabled={busy}
                  onClick={() => call((token) => api.endMaintenance(token, active.id as number))}
                >
                  End now
                </button>
              </>
            )}
          </span>
        </p>
      ) : (
        <p className="muted">Alerts are sent as usual.</p>
      )}
      {scheduled.map((w) => (
        <p key={w.id} className="muted">
          Scheduled: {w.startsAt && dayClock(w.startsAt)} – {w.until && dayClock(w.until)},{" "}
          {w.reason}
        </p>
      ))}
      {isAdmin && !active && (
        <form className="maintenance-form" onSubmit={hold}>
          <label>
            <span className="sr-only">For how long</span>
            <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
              {DURATIONS.map((d) => (
                <option key={d.minutes} value={d.minutes}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <input
            type="text"
            required
            maxLength={200}
            placeholder="Why (e.g. database migration)"
            aria-label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button type="submit" className="primary" disabled={busy || !reason.trim()}>
            Hold alerts
          </button>
        </form>
      )}
      {error && <p className="muted">{error}</p>}
    </section>
  );
}
